import { Tooltip, Button, Card, Typography, Badge, Divider, Space as AntSpace } from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  HomeOutlined,
  ToolOutlined,
  CloudOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Countdown, { CountdownRendererFn, CountdownTimeDelta } from "react-countdown";
import styled from "styled-components";
import AutoPages from "../pages/AutoPages";
import InfoCard from "../pages/InfoCard";
import Page1 from "../pages/Page1";
import Page2 from "../pages/Page2";
import Page3 from "../pages/Page3";
import Space from "./Space";
import GeometricPage from "../pages/GeometricPage";
import NotesPage from "../pages/NotesPage";
import AdditionalPage from "../pages/AdditionalPage";
import { IInvestmentReturnModel, IReportReturnModel } from "../Model";
import { showMoney } from "../Helper";

const { Title, Text } = Typography;

// Definiowanie typów dla argumentów funkcji renderer
interface RendererProps extends CountdownTimeDelta {
  completed: boolean;
}

// Implementacja funkcji renderer z typami
const renderer: CountdownRendererFn = ({ days, hours, minutes, seconds, completed }: RendererProps) => {
  if (completed) {
    return (
      <div style={{ color: "#ff4d4f", fontWeight: 600, fontSize: "16px" }}>
        Oferta straciła ważność. Skontaktuj się z doradcą.
      </div>
    );
  } else {
    return (
      <CountdownGrid>
        <TimeUnit value={days} label="DNI" />
        <TimeUnit value={hours} label="GODZIN" />
        <TimeUnit value={minutes} label="MINUT" />
        <TimeUnit value={seconds} label="SEKUND" />
      </CountdownGrid>
    );
  }
};

const TimeUnit: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      background: "#0555a0",
      borderRadius: "12px",
      padding: "12px 16px",
      minWidth: "70px",
    }}
  >
    <span style={{ fontSize: "28px", fontWeight: "700", color: "#fff", lineHeight: 1.2 }}>{value}</span>
    <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.85)", fontWeight: "500", letterSpacing: "1px" }}>
      {label}
    </span>
  </div>
);

export default function Report(props: any) {
  const navigate = useNavigate();
  let { pathname } = useLocation();

  const [investment, setInvestment] = useState<IInvestmentReturnModel | null>(null);
  const [activeReportCode, setActiveReportCode] = useState(undefined);

  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (props.investment) {
      setInvestment(props.investment);
    }

    if (props.activeReportCode) {
      setActiveReportCode(props.activeReportCode);
    }
  }, [props]);

  const isShortReport = () => {
    return pathname.split("/")[3] === "short";
  };

  // Definicja typów dla funkcji onSuccess i onFailure
  type CallbackFunction = () => void;

  const handleSelectReportClick = (threeClick: CallbackFunction, otherClick: CallbackFunction) => {
    const now = Date.now();
    const timeDifference = now - lastClickTime;

    if (timeDifference < 500) {
      setClickCount((prevCount) => prevCount + 1);

      if (clickCount + 1 === 3) {
        // console.log("3 szybkie kliknięcia!");
        setClickCount(0);
        if (timeoutId) clearTimeout(timeoutId);
        threeClick();
      } else {
        // Resetuj timeout przy każdym kliknięciu, które nie kończy sekwencji
        if (timeoutId) clearTimeout(timeoutId);
        const newTimeoutId = setTimeout(() => {
          // console.log("Sekwencja kliknięć niekompletna.");
          setClickCount(0);
          otherClick();
        }, 500); // Czekaj 500ms na kolejne kliknięcie
        setTimeoutId(newTimeoutId);
      }
    } else {
      // Zresetuj licznik, jeśli kliknięcia są zbyt wolne
      setClickCount(1);
      if (timeoutId) clearTimeout(timeoutId);
      const newTimeoutId = setTimeout(() => {
        // console.log("Sekwencja kliknięć niekompletna.");
        setClickCount(0);
        otherClick();
      }, 500);
      setTimeoutId(newTimeoutId);
    }

    setLastClickTime(now);
  };

  // Funkcja do sprawdzenia, czy urządzenie to urządzenie mobilne
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Responsive scaling for report section
  const REPORT_WIDTH = 800; // szerokość raportu w px
  const reportContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);

  const calculateScale = useCallback(() => {
    const containerWidth = reportContainerRef.current?.parentElement?.clientWidth || window.innerWidth;
    const padding = 32; // padding po bokach
    const availableWidth = containerWidth - padding;
    const newScale = Math.min(1, availableWidth / REPORT_WIDTH);
    setScale(newScale);
  }, []);

  useEffect(() => {
    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, [calculateScale]);

  // Observe content height changes and update container height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateHeight = () => {
      const height = content.scrollHeight * scale;
      setContentHeight(height);
    };

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(content);
    updateHeight();

    return () => resizeObserver.disconnect();
  }, [scale]);

  interface RowButtonProps {
    title: string;
    name: string;
    noContent: boolean;
    icon?: React.ReactNode;
  }

  const RowButton: React.FC<RowButtonProps> = ({ title, name, noContent, icon }) => {
    return (
      <RowButtonContainer style={{ opacity: noContent ? 0.4 : 1 }}>
        <div className="label">
          {icon && <span className="icon">{icon}</span>}
          {title}
        </div>
        <div className="value" title={name}>
          {name}
        </div>
      </RowButtonContainer>
    );
  };

  const getActiveReportExpiryDate = () => {
    return (
      investment?.reports.find((report: IReportReturnModel) => report.reportNumber === activeReportCode)
        ?.expiryDate ||
      investment?.expiryDate ||
      new Date()
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePrintInfoCard = () => {
    const restore = () => {
      document.body.classList.remove("printing-info-card");
      if (reportContainerRef.current) {
        reportContainerRef.current.style.display = "";
      }
    };

    document.body.classList.add("printing-info-card");
    if (reportContainerRef.current) {
      reportContainerRef.current.style.display = "none";
    }

    window.addEventListener("afterprint", restore, { once: true });

    // Fallback przez matchMedia dla Chrome
    const mq = window.matchMedia("print");
    const mqHandler = (e: MediaQueryListEvent) => {
      if (!e.matches) {
        restore();
        mq.removeEventListener("change", mqHandler);
      }
    };
    mq.addEventListener("change", mqHandler);

    window.print();
  };

  const activeReport = investment?.reports.find((r: IReportReturnModel) => r.reportNumber === activeReportCode);

  return (
    <Container>
      {investment && (
        <div style={{ width: "100%" }}>
          {/* Header Section */}
          <OfferHeader>
            <AntSpace direction="vertical" size="large" style={{ width: "100%", textAlign: "center" }}>
              <div>
                <ClockCircleOutlined style={{ fontSize: "32px", color: "#0555a0", marginBottom: "8px" }} />
                <Title level={4} style={{ margin: 0, color: "#434343" }}>
                  Ważność oferty
                </Title>
                <Text type="secondary" style={{ fontSize: "14px" }}>
                  Ta oferta wygasa za:
                </Text>
              </div>
              <Countdown date={new Date(getActiveReportExpiryDate())} renderer={renderer} />
            </AntSpace>
          </OfferHeader>

          <Space />

          {/* Variants Section */}
          <SelectReportContainer>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <Title level={4} style={{ margin: 0, color: "#434343" }}>
                Dostępne warianty kalkulacji
              </Title>
              <Text type="secondary">Wybierz wariant, aby zobaczyć szczegółową ofertę</Text>
            </div>

            <VariantsGrid count={investment.reports.length}>
              {investment.reports.map((item: IReportReturnModel, index: number) => {
                const isActive = item.reportNumber === activeReportCode;
                return (
                  <VariantCard
                    key={index}
                    $isActive={isActive}
                    hoverable
                    onClick={() => {
                      handleSelectReportClick(
                        () => {
                          const code = "short/" + Date.now() + Math.random().toString(36).substr(2, 20);
                          return navigate(`${props.investmentCode}/${item.reportNumber}/${code}`);
                        },
                        () => {
                          navigate(`${props.investmentCode}/${item.reportNumber}`);
                        },
                      );
                    }}
                  >
                    {investment.reports.length > 1 && (
                      <VariantBadge $isActive={isActive}>Wariant {index + 1}</VariantBadge>
                    )}
                    <div>
                      <RowButton
                        icon={<HomeOutlined />}
                        title="System pokrycia"
                        name={item.roofSystemName.length > 0 ? item.roofSystemName : "Brak wybranego systemu"}
                        noContent={item.roofSystemName.length === 0}
                      />
                      <RowButton
                        icon={<CloudOutlined />}
                        title="System orynnowania"
                        name={item.gutterSystemName.length > 0 ? item.gutterSystemName : "Brak wybranego systemu"}
                        noContent={item.gutterSystemName.length === 0}
                      />
                      <RowButton
                        icon={<SafetyCertificateOutlined />}
                        title="System krycia wstępnego"
                        name={
                          item.preCoveringSystemName.length > 0
                            ? item.preCoveringSystemName
                            : "Brak wybranego systemu"
                        }
                        noContent={item.preCoveringSystemName.length === 0}
                      />
                      <RowButton
                        icon={<ToolOutlined />}
                        title="System zamocowań"
                        name={
                          item.fasteningSystemName.length > 0 ? item.fasteningSystemName : "Brak wybranego systemu"
                        }
                        noContent={item.fasteningSystemName.length === 0}
                      />

                      <Divider style={{ margin: "12px 0" }} />

                      <PriceSection>
                        <div className="price-row-inline">
                          <div className="price-item">
                            <Text strong style={{ fontSize: "16px" }}>
                              Netto:
                            </Text>
                            <Text strong style={{ fontSize: "16px", marginLeft: "6px" }}>
                              {showMoney(item.totalNettPrice)} zł
                            </Text>
                          </div>
                          <div className="price-item">
                            <Text strong style={{ fontSize: "16px" }}>
                              VAT:
                            </Text>
                            <Text strong style={{ fontSize: "16px", marginLeft: "6px" }}>
                              {item.vat}%
                            </Text>
                          </div>
                          <div className="price-item">
                            <Text strong style={{ color: "#0555a0", fontSize: "16px" }}>
                              Brutto:
                            </Text>
                            <Text strong style={{ fontSize: "16px", color: "#0555a0", marginLeft: "6px" }}>
                              {showMoney(item.totalPrice)} zł
                            </Text>
                          </div>
                        </div>
                      </PriceSection>
                    </div>
                    {/* </Badge.Ribbon> */}
                  </VariantCard>
                );
              })}
            </VariantsGrid>
          </SelectReportContainer>

          <PrintButtonContainer>
            <Button
              type="primary"
              size="large"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              style={{
                background: "#0555a0",
                border: "none",
                borderRadius: "8px",
                height: "48px",
                padding: "0 24px",
                fontSize: "15px",
                fontWeight: "600",
              }}
            >
              Drukuj wariant
            </Button>
            {activeReport && (
              <Button
                size="large"
                icon={<PrinterOutlined />}
                onClick={handlePrintInfoCard}
                style={{
                  border: "2px solid #0555a0",
                  color: "#0555a0",
                  borderRadius: "8px",
                  height: "48px",
                  padding: "0 24px",
                  fontSize: "15px",
                  fontWeight: "600",
                  background: "#ffffff",
                }}
              >
                Drukuj kartę informacyjną
              </Button>
            )}
          </PrintButtonContainer>

          {activeReport && <InfoCard report={activeReport} investmentNumber={investment?.investmentNumber} />}

          <Space />

          <ReportScalerContainer ref={reportContainerRef} style={{ height: scale < 1 ? contentHeight : "auto" }}>
            <ReportScalerContent ref={contentRef} $scale={scale} $needsScaling={scale < 1}>
              {investment.reports.map((report: IReportReturnModel, index: number) => {
                if (report.reportNumber === activeReportCode) {
                  if (isShortReport()) {
                    return (
                      <div key={index}>
                        {/* Strona z geometrią - jeśłi pełny raport */}
                        {report.isFull && (
                          <div data-section="GeometricPage">
                            <GeometricPage report={report} />
                          </div>
                        )}
                        {report.isFull && <Space />}
                        {/* Produkty */}
                        <AutoPages report={report} short={true} />
                      </div>
                    );
                  } else {
                    return (
                      <div key={index}>
                        {/* Strona 1 - stała */}
                        <div data-section="Page1">
                          <Page1 report={report} />
                        </div>
                        <Space />
                        {/* Strona 2 - stała */}
                        <div data-section="Page2">
                          <Page2 report={report} />
                        </div>
                        <Space />
                        {/* Strona 3 - stała */}
                        <div data-section="Page3">
                          <Page3 report={report} />
                        </div>
                        <Space />
                        {/* Produkty */}
                        <AutoPages report={report} short={false} />
                        {/* Strona z geometrią - stała */}
                        {report.isFull && (
                          <div data-section="GeometricPage">
                            <GeometricPage report={report} />
                          </div>
                        )}
                        {report.isFull && <Space />}
                        {/* Strona z uwagami - stała */}
                        <div data-section="NotesPage">
                          <NotesPage report={report} />
                        </div>
                        <Space />
                        {/* W pętli lista stron dodatkowych */}
                        {report.additionalPages.map((page, index) => {
                          return (
                            <div key={index} data-section={`AdditionalPage_${index + 1}`}>
                              <AdditionalPage page={page} />
                              <Space />
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                }
              })}
            </ReportScalerContent>
          </ReportScalerContainer>
        </div>
      )}

      {investment?.userPhone && (investment.userPhone.match(/\d/g) || []).length >= 9 && (
        <Tooltip
          title={
            <span>
              Zadzwoń do doradcy technicznego! Telefon: <b>+48 {investment?.userPhone}</b>
            </span>
          }
          placement="topRight"
          color={"#0555a0"}
        >
          <CallContainer>
            <PhoneOutlined style={{ fontSize: "16px" }} />
            <a
              href={isMobileDevice() ? `tel:+48${investment?.userPhone}` : "#"}
              style={{ color: "inherit", textDecoration: "none" }}
            >
              Doradca techniczny
            </a>
          </CallContainer>
        </Tooltip>
      )}
    </Container>
  );
}

const Container = styled.div`
  margin: auto;
  width: 100%;
  overflow-x: hidden;
  max-width: 100vw;

  @media print {
    overflow: visible !important;
    max-width: none !important;
  }
`;

const CountdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 12px;
  justify-content: center;

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, auto);
  }
`;

const OfferHeader = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 32px 16px 0;
  box-sizing: border-box;

  @media print {
    display: none;
  }
`;

const SelectReportContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  padding: 24px 16px;
  box-sizing: border-box;

  @media print {
    display: none;
  }
`;

const VariantsGrid = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.count === 1 ? "1fr" : props.count === 2 ? "repeat(2, 1fr)" : "repeat(3, 1fr)"};
  gap: 20px;
  width: 100%;
  max-width: ${(props) => (props.count === 1 ? "500px" : "1200px")};
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: ${(props) => (props.count === 1 ? "1fr" : "repeat(2, 1fr)")};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 100%;
  }
`;

const RowButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: none;
  gap: 72px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .label {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    color: #8c8c8c;
    gap: 6px;
    flex-shrink: 0;
    min-width: fit-content;

    .icon {
      color: #0555a0;
    }
  }

  .value {
    font-size: 13px;
    font-weight: 600;
    color: #262626;
    text-align: right;
    flex: 1;
    line-height: 1.4;
    word-break: break-word;

    @media (max-width: 768px) {
      text-align: left;
    }
  }
`;

const VariantCard = styled(Card)<{ $isActive: boolean }>`
  border-radius: 16px !important;
  border: 2px solid ${(props) => (props.$isActive ? "#0555a0" : "#e8e8e8")} !important;
  box-shadow: none !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
  background: ${(props) => (props.$isActive ? "#f0f5fa" : "#fff")} !important;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 20px;

  &:hover {
    border-color: #0555a0 !important;
    box-shadow: none !important;
    transform: translateY(-4px);
  }

  .ant-card-body {
    padding: 16px;
    box-sizing: border-box;
  }

  .ant-ribbon {
    top: -4px;
  }
`;

const ActiveBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: #52c41a;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 10;
`;

const VariantBadge = styled.div<{ $isActive: boolean }>`
  position: absolute;
  top: -14px;
  right: 16px;
  background: ${(props) => (props.$isActive ? "#0555a0" : "#8c8c8c")};
  color: white;
  padding: 6px 20px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  z-index: 10;
  white-space: nowrap;
`;

const PriceSection = styled.div`
  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;

    &.total {
      padding-top: 8px;
      margin-top: 4px;
      border-top: 1px dashed #d9d9d9;
    }
  }

  .price-row-inline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .price-item {
      display: flex;
      align-items: center;
      white-space: nowrap;
    }
  }
`;

const PrintButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 8px 0 8px;

  @media print {
    display: none;
  }
`;

const CallContainer = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  padding: 14px 20px;
  background: #0555a0;
  color: #fff;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  @media print {
    display: none;
  }

  &:hover {
    transform: translateY(-2px);
  }

  a {
    color: inherit;
    text-decoration: none;
  }
`;

// Responsive scaling containers for report content
const ReportScalerContainer = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  position: relative;

  @media print {
    height: auto !important;
    overflow: visible !important;
  }
`;

const ReportScalerContent = styled.div<{ $scale: number; $needsScaling: boolean }>`
  transform: ${(props) => (props.$needsScaling ? `scale(${props.$scale})` : "none")};
  transform-origin: top center;
  width: ${(props) => (props.$needsScaling ? `${100 / props.$scale}%` : "100%")};
  position: ${(props) => (props.$needsScaling ? "absolute" : "relative")};
  top: 0;

  @media print {
    transform: none !important;
    width: auto !important;
    position: relative !important;
    top: auto !important;
  }
`;
